/**
 * Form State Management Hook
 *
 * Refactored version of useFormState that uses FormStateManagementService
 * for centralized state management with caching and performance optimizations.
 *
 * Maintains 100% backward compatibility with the original useFormState interface
 * while leveraging the new FormStateManagementService for improved testability and performance.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { FormStateManagementService, FormState } from '../services/FormStateManagementService';
import { logDebug } from '../utils/utils';

// Hook return type for backward compatibility
interface UseFormStateManagementReturn {
    isCustomDirty: boolean;
    isDesignChange: boolean;
    isOnlyDesignSectionChange: (values: any, initialValues: any) => boolean;
    updateFormState: (dirty: boolean, values: any, initialValues: any) => void;
    isSubmittingDesign: boolean;
    // 🚀 NEW: State machine enhanced methods
    canRedirect: boolean;
    shouldAutoSubmit: boolean;
    getStateMachineState: () => string;
    forceReset: () => boolean;
}

/**
 * Custom hook for form state management using FormStateManagementService
 *
 * This hook replaces the original useFormState and provides the same interface
 * while using the new centralized FormStateManagementService for improved performance
 * and testability.
 *
 * @returns Object with form state properties and update functions
 */
export const useFormStateManagement = (): UseFormStateManagementReturn => {
    // Get singleton service instance
    const formStateService = useMemo(() => FormStateManagementService.getInstance(), []);

    // Local state synchronized with service
    const [formState, setFormState] = useState<FormState>(() => formStateService.getFormState());

    // Subscribe to service state changes for React state synchronization
    useEffect(() => {
        const unsubscribe = formStateService.subscribeToFormState(newState => {
            setFormState(newState);
            logDebug('[useFormStateManagement]', 'Form state updated from service', newState);
        });

        return unsubscribe;
    }, [formStateService]);

    // Initialize with current service state on mount only
    useEffect(() => {
        setFormState(formStateService.getFormState());
    }, [formStateService]);

    // Memoized update function that uses the service
    const updateFormState = useCallback(
        (dirty: boolean, values: any, initialValues: any) => {
            formStateService.updateFormState(dirty, values, initialValues);
        },
        [formStateService]
    );

    // Memoized design-only change detection function
    const isOnlyDesignSectionChange = useCallback(
        (values: any, initialValues: any): boolean => {
            return formStateService.isDesignOnlyChange(values, initialValues);
        },
        [formStateService]
    );

    // Memoized function to control submission state
    const setSubmittingDesign = useCallback(
        (isSubmitting: boolean) => {
            formStateService.setSubmittingDesign(isSubmitting);
        },
        [formStateService]
    );

    // 🚀 NEW: State machine enhanced methods
    const canRedirect = useMemo(() => formStateService.canRedirect(), [formStateService, formState]);
    const shouldAutoSubmit = useMemo(() => formStateService.shouldAutoSubmit(), [formStateService, formState]);

    const getStateMachineState = useCallback(() => {
        return formStateService.getStateMachineState();
    }, [formStateService]);

    const forceReset = useCallback(() => {
        return formStateService.forceReset();
    }, [formStateService]);

    return {
        isCustomDirty: formState.isCustomDirty,
        isDesignChange: formState.isDesignChange,
        isOnlyDesignSectionChange,
        updateFormState,
        isSubmittingDesign: formState.isSubmittingDesign,
        // 🚀 NEW: State machine enhanced methods
        canRedirect,
        shouldAutoSubmit,
        getStateMachineState,
        forceReset,
    };
};

/**
 * Legacy export for backward compatibility
 * This allows existing code to import useFormState and get the new implementation
 */
export const useFormState = useFormStateManagement;

export default useFormStateManagement;
